import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ChevronRight,
  CheckCircle2,
  ArrowRight,
  LucideIcon
} from 'lucide-react';

export interface OnboardingStep {
  id: number;
  icon: LucideIcon;
  title: string;
  description: string;
  highlight: string;
  color: string;
}

export interface OnboardingBenefit {
  icon: LucideIcon;
  text: string;
}

interface ModuleOnboardingProps {
  onComplete: () => void;
  steps: OnboardingStep[];
  benefits?: OnboardingBenefit[];
  ctaText?: string;
}

export default function ModuleOnboarding({ 
  onComplete, 
  steps, 
  benefits = [],
  ctaText = 'Comenzar'
}: ModuleOnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsExiting(true);
      setTimeout(() => {
        onComplete();
      }, 300);
    }
  };

  const handleSkip = () => {
    setIsExiting(true);
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className={`min-h-[calc(100vh-8rem)] flex items-center justify-center transition-opacity duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
      <div className="max-w-lg w-full space-y-8 p-6">
        {/* Progress dots */}
        <div className="flex justify-center gap-2">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? 'w-8 bg-primary' 
                  : index < currentStep 
                    ? 'w-2 bg-primary/60' 
                    : 'w-2 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        {/* Main card with animation */}
        <Card 
          key={currentStep}
          className="glass-card border-border/50 overflow-hidden animate-in fade-in slide-in-from-right-4 duration-500"
        >
          <CardContent className="pt-8 pb-8 space-y-6">
            {/* Icon with gradient background */}
            <div className="flex justify-center">
              <div className={`relative w-24 h-24 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg transform transition-transform hover:scale-105`}>
                <StepIcon className="w-12 h-12 text-white" />
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-white/20 to-transparent opacity-50" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center space-y-3">
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                Paso {step.id} de {steps.length}
              </span>
              <h2 className="text-2xl font-bold text-foreground">{step.title}</h2>
              <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              <div className="pt-2">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  {step.highlight}
                </span>
              </div>
            </div>

            {/* Benefits on last step */}
            {isLastStep && benefits.length > 0 && (
              <div className="space-y-3 pt-4 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
                <p className="text-sm text-center text-muted-foreground">Al completar este módulo obtendrás:</p>
                <div className="grid gap-2">
                  {benefits.map((benefit, index) => {
                    const BenefitIcon = benefit.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/50"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <BenefitIcon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-sm text-foreground">{benefit.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="flex gap-3">
          {!isLastStep && (
            <Button 
              variant="ghost" 
              onClick={handleSkip}
              className="flex-1 text-muted-foreground hover:text-foreground"
            >
              Saltar
            </Button>
          )}
          <Button 
            onClick={handleNext}
            className={`gradient-primary ${isLastStep ? 'w-full' : 'flex-1'}`}
          >
            {isLastStep ? (
              <>
                {ctaText}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Siguiente
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>

        {/* Skip hint */}
        {!isLastStep && (
          <p className="text-center text-xs text-muted-foreground/60">
            Presiona las teclas ← → para navegar
          </p>
        )}
      </div>
    </div>
  );
}
